import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";
import type { NavigateOptions } from "react-router-dom";

export type RouterViewTransitionsConfig = {
  /**
   * When enabled, `Link` and `navigate` helpers in `@atoms/atom-components`
   * will default to using React Router's View Transitions API.
   *
   * Defaults to false to avoid surprising behavior in consuming apps.
   */
  enabled: boolean;
};

const RouterViewTransitionsContext = createContext<RouterViewTransitionsConfig>(
  {
    enabled: false,
  },
);

export function RouterViewTransitionsProvider(
  props: PropsWithChildren<Partial<RouterViewTransitionsConfig>>,
) {
  const value = useMemo<RouterViewTransitionsConfig>(
    () => ({
      enabled: props.enabled ?? false,
    }),
    [props.enabled],
  );

  return (
    <RouterViewTransitionsContext.Provider value={value}>
      {props.children}
    </RouterViewTransitionsContext.Provider>
  );
}

export function useRouterViewTransitionsConfig() {
  return useContext(RouterViewTransitionsContext);
}

export function mergeRouterViewTransitionOptions(
  options: NavigateOptions | undefined,
  enabled: boolean,
): NavigateOptions | undefined {
  if (!enabled) {
    return options;
  }

  return {
    ...options,
    viewTransition: options?.viewTransition ?? true,
  };
}
